import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { Checkbox, Form, Input, InputNumber, Radio, Slider, Tooltip } from "antd";
import { useEffect, useMemo, type ComponentType, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
// import DeleteIcon from "sipli-fleet-core/assets/icons/delete-icon.svg";
import DeleteIcon from "sipli-fleet-core/assets/icons/cross-icon.svg";
import { CustomInput, CustomSelect } from "sipli-fleet-core/components";

import {
  addFilterOptions,
  convertOptions,
  driverData,
  driverIdFormat,
  externalPowerEventsOptions,
  fieldRelayIdOptions,
  fieldSourceOptions,
  fieldTypeOptions,
  fuelTankIdFieldOptions,
  signalStrengthFieldTypeOption,
  switchStateUniqueIdOption,
  temperatureTypeData,
  temperatureUnitData,
} from "./mapping.helpers";
import type {
  IncomingValueOption,
  MappingFilterRecord,
  MappingFormPrimitive,
  MappingRecord,
  MappingSelectOption,
} from "./mapping.types";

interface MappingFiltersProps {
  filters: MappingFilterRecord[];
  onAddFilter: (filterId: string) => void;
  onRemoveFilter: (uniqueId?: string) => void;
  onReorderFilters: (sourceIndex: number, destinationIndex: number) => void;
  variant?: "modal" | "drawer" | "add-modal";
}

interface MappingEditorFieldsProps {
  incomingOptions: IncomingValueOption[];
  watchedFilters: MappingFilterRecord[];
  watchedMappingType?: string;
  watchedLinkedToFuel: boolean;
  watchedTargetCheckbox: boolean;
  previewValue: MappingFormPrimitive;
  relayOptions: MappingSelectOption[];
  switchOptions: MappingSelectOption[];
  temperatureTypeOptions?: MappingSelectOption[];
  mappings?: MappingRecord[];
  editingMapping?: MappingRecord | null;
  onAddFilter: (filterId: string) => void;
  onRemoveFilter: (uniqueId?: string) => void;
  onReorderFilters: (sourceIndex: number, destinationIndex: number) => void;
  variant?: "modal" | "drawer" | "add-step-one" | "add-step-two";
}

const selectOptions = (options: MappingSelectOption[]) => options;
const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{
  className?: string;
}>;
const numberInputClassName = "!w-full !max-w-full !min-w-0 !rounded-[2px]";
const textInputClassName = "!mt-0 !h-[32px] !py-0 common-input";
const formatFeaturedValue = (value: MappingFormPrimitive) => {
  if (value === undefined || value === null) return "0";
  return String(value);
};

export const MappingEditorFields = ({
  incomingOptions,
  watchedFilters,
  watchedMappingType,
  watchedLinkedToFuel,
  watchedTargetCheckbox,
  previewValue,
  relayOptions,
  switchOptions,
  temperatureTypeOptions = [],
  mappings = [],
  editingMapping = null,
  onAddFilter,
  onRemoveFilter,
  onReorderFilters,
  variant = "modal",
}: MappingEditorFieldsProps) => {
  const { t } = useTranslation();

  if (variant === "add-step-one") {
    return (
      <div>
        <Form.Item
          name="value"
          label={t("field", { defaultValue: "Field" })}
          rules={[
            {
              required: true,
              message: t("field_is_required", {
                defaultValue: "Field is required",
              }),
            },
          ]}
        >
          <CustomSelect
            options={incomingOptions.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            showSearch
            optionFilterProp="label"
            placeholder={t("select_option")}
            className="common-select"
          />
        </Form.Item>

        <div className="grid grid-cols-1 gap-x-4 gap-y-0 md:grid-cols-2">
          <MappingTypeFields
            mappingType={watchedMappingType}
            incomingOptions={incomingOptions}
            relayOptions={relayOptions}
            switchOptions={switchOptions}
            temperatureTypeOptions={temperatureTypeOptions}
            mappings={mappings}
            editingMapping={editingMapping}
            linkedToFuel={watchedLinkedToFuel}
            variant="add-step-one"
          />
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-3">
          <Form.Item name="realtime" valuePropName="checked" className="!mb-0 md:pb-[7px]">
            <Checkbox>{t("realtime")}</Checkbox>
          </Form.Item>
          <span className="text-[14px] leading-[18px] text-gray9">{t("forward_this_target")}:</span>
          <Form.Item name="targetCheckbox" valuePropName="checked" className="!mb-0">
            <Checkbox />
          </Form.Item>
          {watchedTargetCheckbox && (
            <div className="flex min-w-[220px] flex-1 items-center gap-3">
              <span className="text-[14px] leading-[18px] text-gray9">{t("to")}:</span>
              <Form.Item name="targetInput" className="!mb-0 flex-1">
                <CustomInput className={textInputClassName} placeholder="Car" />
              </Form.Item>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (variant === "add-step-two") {
    return (
      <div className="mt-8">
        <Form.Item name="selectMapping" hidden preserve>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="value" hidden preserve>
          <Input type="hidden" />
        </Form.Item>

        <MappingFilters
          filters={watchedFilters}
          onAddFilter={onAddFilter}
          onRemoveFilter={onRemoveFilter}
          onReorderFilters={onReorderFilters}
          variant="add-modal"
        />

        <div className="mt-5 grid grid-cols-1 gap-4 items-center md:grid-cols-[minmax(0,2fr)_minmax(0,0.9fr)]">
          <Form.Item name="lastValue" label={t("lastValue")} className="!mb-0">
            <CustomInput className={textInputClassName} />
          </Form.Item>

          <div>
            <div className="mb-[8px] text-[14px] leading-[18px] text-gray8">
              {t("featured_value")}
            </div>
            <div className="min-h-[32px] pt-[6px] text-[14px] leading-[18px] text-gray10">
              {formatFeaturedValue(previewValue)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "drawer") {
    return (
      <div className="rounded-[4px] px-[14px] py-[16px]">
        <div className="mb-[20px]">
          <div className="mb-[14px] text-[15.75px] font-bold leading-[20px] text-gray10">
            {t("mapping_fields")}
          </div>
          <Form.Item name="value" hidden>
            <Input type="hidden" />
          </Form.Item>
          {/* <Form.Item
            name="value"
            label={t("value")}
            rules={[{ required: true, message: t("value_is_required") }]}
          >
            <Select
              options={incomingOptions.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item> */}
          <div className="grid grid-cols-2 gap-x-[14px] gap-y-[10px]">
            <MappingTypeFields
              mappingType={watchedMappingType}
              incomingOptions={incomingOptions}
              relayOptions={relayOptions}
              switchOptions={switchOptions}
              temperatureTypeOptions={temperatureTypeOptions}
              mappings={mappings}
              editingMapping={editingMapping}
              linkedToFuel={watchedLinkedToFuel}
              variant="drawer"
            />
          </div>
          <Form.Item name="realtime" valuePropName="checked" className="!mb-0 !mt-[4px]">
            <Checkbox>{t("realtime")}</Checkbox>
          </Form.Item>
          <div className="mt-[12px] flex flex-wrap items-center gap-3">
            <span className="text-[14px] leading-[18px] text-gray9">
              {t("forward_this_target")}:
            </span>
            <Form.Item name="targetCheckbox" valuePropName="checked" noStyle>
              <Checkbox />
            </Form.Item>
            {watchedTargetCheckbox && (
              <>
                <span className="text-[14px] leading-[18px] text-gray9">{t("to")}:</span>
                <Form.Item name="targetInput" noStyle>
                  <Input className="!w-[180px]" placeholder="Car" />
                </Form.Item>
              </>
            )}
          </div>
        </div>

        <div className="mb-[20px] border-t border-[#E5EAF5] pt-[18px]">
          <div className="mb-[14px] text-[15.75px] font-bold leading-[20px] text-gray10">
            {t("filters")}
          </div>
          <MappingFilters
            filters={watchedFilters}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onReorderFilters={onReorderFilters}
            variant="drawer"
          />
        </div>

        <div className="grid grid-cols-2 gap-[14px] border-t border-[#E5EAF5] pt-[18px]">
          <Form.Item name="lastValue" label={t("lastValue")} className="!mb-0">
            <Input />
          </Form.Item>
          <div>
            <div className="mb-[8px] text-[14px] leading-[18px] text-gray8">
              {t("featured_value")}
            </div>
            <div className="min-h-[32px] pt-[6px] text-[14px] leading-[18px] text-gray10">
              {formatFeaturedValue(previewValue)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-[1fr_120px] gap-4">
        <Form.Item name="value" hidden>
          <Input type="hidden" />
        </Form.Item>
        {/* <Form.Item
          name="value"
          label={t("value")}
          rules={[{ required: true, message: t("value_is_required") }]}
        >
          <Select
            options={incomingOptions.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item> */}
        <Form.Item name="realtime" valuePropName="checked" label={t("realtime")}>
          <Checkbox />
        </Form.Item>
      </div>

      <MappingFilters
        filters={watchedFilters}
        onAddFilter={onAddFilter}
        onRemoveFilter={onRemoveFilter}
        onReorderFilters={onReorderFilters}
      />

      <Form.Item name="lastValue" label={t("lastValue")}>
        <Input />
      </Form.Item>
      <div className="mb-4 text-sm text-gray9">
        <span className="font-semibold text-gray10">{t("featured_value")}:</span>{" "}
        {formatFeaturedValue(previewValue)}
      </div>

      <MappingTypeFields
        mappingType={watchedMappingType}
        incomingOptions={incomingOptions}
        relayOptions={relayOptions}
        switchOptions={switchOptions}
        temperatureTypeOptions={temperatureTypeOptions}
        mappings={mappings}
        editingMapping={editingMapping}
        linkedToFuel={watchedLinkedToFuel}
      />

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="font-semibold">{t("forward_this_target")}:</span>
        <Form.Item name="targetCheckbox" valuePropName="checked" noStyle>
          <Checkbox />
        </Form.Item>
        {watchedTargetCheckbox && (
          <>
            <span>{t("to")}:</span>
            <Form.Item name="targetInput" noStyle>
              <Input className="!w-[220px]" placeholder="Car" />
            </Form.Item>
          </>
        )}
      </div>
    </>
  );
};

const MappingFilters = ({
  filters = [],
  onAddFilter,
  onRemoveFilter,
  onReorderFilters,
  variant = "modal",
}: MappingFiltersProps) => {
  const { t } = useTranslation();
  const isAddModal = variant === "add-modal";
  const getFilterName = (filterId?: string) =>
    t(`mapping_filter_${filterId}`, {
      defaultValue: addFilterOptions.find((item) => item.id === filterId)?.name ?? "",
    });
  const getFilterLabel = (filterId?: string) =>
    isAddModal
      ? getFilterName(filterId)
      : addFilterOptions.find((item) => item.id === filterId)?.name;

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    onReorderFilters(result.source.index, result.destination.index);
  };

  return (
    <div className={variant === "drawer" ? "" : "mb-4"}>
      {(variant === "drawer" || isAddModal) && (
        <div className="mb-[8px] text-[14px] leading-[18px] text-gray8">{t("add_filter")}</div>
      )}
      <CustomSelect
        key={filters.length}
        className="!w-full common-select"
        placeholder={t("add_filter")}
        value={undefined}
        options={addFilterOptions.map((option) => ({
          value: option.id,
          label: getFilterLabel(option.id),
        }))}
        onSelect={(filterId) => onAddFilter(String(filterId))}
        showSearch
        optionFilterProp="label"
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="mapping-filters">
          {(dropProvided) => (
            <div
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              className={
                isAddModal ? "mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3" : "mt-3 space-y-3"
              }
            >
              {filters?.map((filter, index) => {
                const uniqueId = filter?.uniqueId ?? filter?.id;

                return (
                  <Draggable key={uniqueId} draggableId={uniqueId} index={index}>
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={dragProvided.draggableProps.style}
                        className={
                          variant === "drawer"
                            ? "relative cursor-move rounded-[16px] bg-gray1 px-[18px] pb-[16px] pt-[16px] transition-all duration-150"
                            : isAddModal
                              ? "relative w-[223.33px] cursor-move rounded-[14px] bg-gray1 px-[16px] pb-[16px] pt-[22px] transition-all duration-150"
                              : "relative cursor-move rounded border border-gray5 p-3 transition-all duration-150"
                        }
                      >
                        <button
                          type="button"
                          onClick={() => onRemoveFilter(uniqueId)}
                          className={
                            variant === "drawer" || isAddModal
                              ? "border-2 border-[#B2B8CF] rounded-sm p-[4px] !absolute right-[14px] top-[12px] flex h-[24px] w-[24px] cursor-pointer items-center justify-center text-red-500 transition-opacity hover:opacity-75"
                              : "!absolute right-2 top-2 cursor-pointer transition-opacity hover:opacity-75"
                          }
                        >
                          <DeleteIconComponent className="h-[16px] w-[15px]" />
                        </button>
                        <div
                          className={`
                          ${
                            variant === "drawer"
                              ? "pr-6 text-[15.75px] font-bold leading-[20px] text-gray10"
                              : isAddModal
                                ? "pr-6 text-[14px] font-[600] leading-[18px] text-black"
                                : "mb-3 font-semibold"
                          }
                          ${filter.id !== "invert_value" ? "mb-[10px]" : ""}
                        `}
                        >
                          {getFilterLabel(filter.id)}
                        </div>
                        <FilterFields filter={filter} variant={variant} />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

const FilterFields = ({
  filter,
  variant = "modal",
}: {
  filter: MappingFilterRecord;
  variant?: "modal" | "drawer" | "add-modal";
}) => {
  const { t } = useTranslation();
  const uniqueId = filter.uniqueId ?? filter.id;
  const isAddModal = variant === "add-modal";

  if (filter.id === "adjust_value_offset_multiply") {
    return (
      <div>
        <div
          className={
            isAddModal
              ? "mb-5 text-[14px] leading-[18px] text-gray10"
              : "mb-5 text-[14px] leading-[20px] text-gray9"
          }
        >
          {t("adjust_value_offset_multiply_description")}
        </div>
        <div className={isAddModal ? "grid grid-cols-1 gap-1.5" : "grid grid-cols-3 gap-4"}>
          <div className={isAddModal ? "flex flex-col gap-0.5" : "flex flex-col gap-2"}>
            <div
              className={
                isAddModal
                  ? "text-[12.4px] font-[600] leading-[18px] text-gray10"
                  : "text-[14px] leading-[18px] text-gray9"
              }
            >
              {t("offset")}
            </div>

            <Form.Item
              name={`${uniqueId}_offset`}
              initialValue={0}
              rules={[{ required: true, message: t("offset_is_required") }]}
              className="!mb-0 min-w-0"
            >
              <InputNumber className={numberInputClassName} />
            </Form.Item>
          </div>
          <div className={isAddModal ? "flex flex-col gap-0.5" : "flex flex-col gap-2"}>
            <div
              className={
                isAddModal
                  ? "text-[12.4px] font-[600] leading-[18px] text-gray10"
                  : "text-[14px] leading-[18px] text-gray9"
              }
            >
              {t("multiplier")}
            </div>
            <Form.Item
              name={`${uniqueId}_multiplier`}
              initialValue={1}
              rules={[{ required: true, message: t("multiplier_is_required") }]}
              className="!mb-0 min-w-0"
            >
              <InputNumber className={numberInputClassName} />
            </Form.Item>
          </div>
        </div>
      </div>
    );
  }

  if (filter.id === "adjust_value_if_statement") {
    return (
      <div>
        <div className="mb-5 text-[14px] leading-[20px] text-gray9">{t("if_statement")}</div>
        <div className={isAddModal ? "grid grid-cols-1 gap-1.5" : "grid grid-cols-3 gap-4"}>
          <div className={isAddModal ? "flex flex-col gap-0.5" : "flex flex-col gap-2"}>
            <div
              className={
                isAddModal
                  ? "text-[12.4px] font-[600] leading-[18px] text-gray10"
                  : "text-[14px] leading-[18px] text-gray9"
              }
            >
              {t("if")}
            </div>
            <Form.Item
              name={`${uniqueId}_operator`}
              rules={[{ required: true, message: t("if_is_required") }]}
              className="!mb-0 min-w-0"
            >
              <CustomSelect
                options={[
                  { value: "greater_than", label: ">" },
                  { value: "less_than", label: "<" },
                  { value: "greater_than_equal", label: ">=" },
                  { value: "less_than_equal", label: "<=" },
                  { value: "equal", label: "=" },
                ]}
                className="common-select"
              />
            </Form.Item>
          </div>

          <div className={isAddModal ? "flex flex-col gap-0.5" : "flex flex-col gap-2"}>
            <div
              className={
                isAddModal
                  ? "text-[12.4px] font-[600] leading-[18px] text-gray10"
                  : "text-[14px] leading-[18px] text-gray9"
              }
            >
              {t("value")}
            </div>
            <Form.Item
              name={`${uniqueId}_param`}
              initialValue={0}
              rules={[{ required: true, message: t("value_is_required") }]}
              className="!mb-0 min-w-0"
            >
              <InputNumber className={numberInputClassName} />
            </Form.Item>
          </div>
          <div className={isAddModal ? "flex flex-col gap-0.5" : "flex flex-col gap-2"}>
            <div
              className={
                isAddModal
                  ? "text-[12.4px] font-[600] leading-[18px] text-gray10"
                  : "text-[14px] leading-[18px] text-gray9"
              }
            >
              {t("then")}
            </div>
            <Form.Item
              name={`${uniqueId}_transformedValue`}
              initialValue={0}
              rules={[{ required: true, message: t("than_is_required") }]}
              className="!mb-0 min-w-0"
            >
              <InputNumber className={numberInputClassName} />
            </Form.Item>
          </div>
        </div>
      </div>
    );
  }

  if (filter.id === "invert_value") {
    return null;
  }

  if (filter.id === "allowed_range_inclusive") {
    return (
      <div className={isAddModal ? "grid grid-cols-1 gap-1.5" : "grid grid-cols-3 gap-4"}>
        <div className={isAddModal ? "flex flex-col gap-0.5" : "flex flex-col gap-2"}>
          <div
            className={
              isAddModal
                ? "text-[12.4px] font-[600] leading-[18px] text-gray10"
                : "text-[14px] leading-[18px] text-gray9"
            }
          >
            {t("min_value")}
          </div>
          <Form.Item
            name={`${uniqueId}_minValue`}
            initialValue={0}
            rules={[{ required: true, message: t("min_value_is_required") }]}
            className="!mb-0 min-w-0"
          >
            <InputNumber className={numberInputClassName} />
          </Form.Item>
        </div>

        <div className={isAddModal ? "flex flex-col gap-0.5" : "flex flex-col gap-2"}>
          <div
            className={
              isAddModal
                ? "text-[12.4px] font-[600] leading-[18px] text-gray10"
                : "text-[14px] leading-[18px] text-gray9"
            }
          >
            {t("max_value")}
          </div>
          <Form.Item
            name={`${uniqueId}_maxValue`}
            initialValue={0}
            rules={[{ required: true, message: t("max_value_is_required") }]}
            className="!mb-0 min-w-0"
          >
            <InputNumber className={numberInputClassName} />
          </Form.Item>
        </div>

        <div className={isAddModal ? "flex flex-col gap-0.5" : "flex flex-col gap-2 col-span-3"}>
          <div
            className={
              isAddModal
                ? "text-[12.4px] font-[600] leading-[18px] text-gray10"
                : "text-[14px] leading-[18px] text-gray9"
            }
          >
            {t("return_nearest_valid_value")}:
          </div>
          <Form.Item name={`${uniqueId}_returnNearest`} initialValue={false} className="!mb-0">
            <Radio.Group
              options={[
                { value: true, label: t("yes") },
                { value: false, label: t("no") },
              ]}
            />
          </Form.Item>
        </div>
      </div>
    );
  }

  return null;
};

interface MappingTypeFieldsProps {
  mappingType?: string;
  incomingOptions: IncomingValueOption[];
  relayOptions: MappingSelectOption[];
  switchOptions: MappingSelectOption[];
  temperatureTypeOptions?: MappingSelectOption[];
  mappings?: MappingRecord[];
  editingMapping?: MappingRecord | null;
  linkedToFuel: boolean;
  variant?: "modal" | "drawer" | "add-step-one";
}

const medianWidthMarks = {
  0: "OFF",
  3: "3",
  5: "5",
  7: "7",
  9: "9",
  13: "13",
  15: "15",
  17: "17",
  19: "19",
  21: "21",
};

const getMappingFieldValue = (mapping: MappingRecord, fieldName: string) => {
  const value = mapping.mappingFields?.[fieldName];
  return value === undefined || value === null ? "" : String(value);
};

const getIncomingFieldName = (incomingOptions: IncomingValueOption[], packetCode?: string) => {
  const incomingOption = incomingOptions.find(
    (item) => String(item.packetCode) === String(packetCode)
  );

  return incomingOption?.name || `Packet Code ${packetCode ?? ""}`;
};

const getUsedFieldNameMap = (
  mappings: MappingRecord[],
  fieldName: string,
  incomingOptions: IncomingValueOption[],
  editingMapping?: MappingRecord | null,
  mappingTypeId?: string
) => {
  const usedMap = new Map<string, string>();

  mappings
    .filter((mapping) => String(mapping.id) !== String(editingMapping?.id ?? ""))
    .filter((mapping) => !mappingTypeId || mapping.mappingTypeId === mappingTypeId)
    .forEach((mapping) => {
      const usedValue = getMappingFieldValue(mapping, fieldName);
      if (!usedValue) return;

      const fieldLabel = getIncomingFieldName(incomingOptions, mapping.packetCode);
      usedMap.set(usedValue, fieldLabel);
    });

  return usedMap;
};

const getDisabledOptionTooltip = (fieldName?: string) =>
  fieldName ? `Field ${fieldName} use this value` : "";

const withDisabledOptions = (
  options: MappingSelectOption[],
  usedFieldNameMap: Map<string, string>
) =>
  options.map((option) => {
    const fieldName = usedFieldNameMap.get(String(option.value));
    const tooltipTitle = getDisabledOptionTooltip(fieldName);
    const isDisabled = Boolean(fieldName);

    return {
      ...option,
      disabled: isDisabled,
      title: tooltipTitle,
      label: isDisabled ? (
        <Tooltip title={tooltipTitle} placement="right">
          <span className="block w-full cursor-not-allowed">{option.label as ReactNode}</span>
        </Tooltip>
      ) : (
        option.label
      ),
    };
  });

const MappingTypeFields = ({
  mappingType,
  incomingOptions,
  relayOptions,
  switchOptions,
  temperatureTypeOptions = [],
  mappings = [],
  editingMapping = null,
  linkedToFuel,
  variant = "modal",
}: MappingTypeFieldsProps) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const selectedTachoDriver = Form.useWatch("driver_id_driver", form);
  const selectedTachoType = Form.useWatch("driver_id_format", form);

  const relayUsedFieldMap = useMemo(
    () => getUsedFieldNameMap(mappings, "relayId", incomingOptions, editingMapping),
    [editingMapping, incomingOptions, mappings]
  );
  const switchUsedFieldMap = useMemo(
    () => getUsedFieldNameMap(mappings, "switchStateUniqueId", incomingOptions, editingMapping),
    [editingMapping, incomingOptions, mappings]
  );
  const fuelTankUsedFieldMap = useMemo(
    () => getUsedFieldNameMap(mappings, "fuelTankIdField", incomingOptions, editingMapping),
    [editingMapping, incomingOptions, mappings]
  );
  const temperatureUniqueIdUsedFieldMap = useMemo(
    () =>
      getUsedFieldNameMap(
        mappings,
        "field_tank_ID",
        incomingOptions,
        editingMapping,
        "temperature"
      ),
    [editingMapping, incomingOptions, mappings]
  );

  const selectedDriverValue = selectedTachoDriver ? String(selectedTachoDriver) : "";
  const usedTachoTypesForDriver = useMemo(() => {
    const usedMap = new Map<string, string>();

    mappings
      .filter((mapping) => String(mapping.id) !== String(editingMapping?.id ?? ""))
      .filter((mapping) => mapping.mappingTypeId === "tacho_card_id")
      .filter((mapping) => String(mapping.mappingFields?.driver ?? "") === selectedDriverValue)
      .forEach((mapping) => {
        const type = String(mapping.mappingFields?.type ?? "");
        if (!type) return;

        usedMap.set(type, getIncomingFieldName(incomingOptions, mapping.packetCode));
      });

    return usedMap;
  }, [editingMapping, incomingOptions, mappings, selectedDriverValue]);

  useEffect(() => {
    if (!selectedTachoType) return;
    if (usedTachoTypesForDriver.has(String(selectedTachoType))) {
      form.setFieldValue("driver_id_format", undefined);
    }
  }, [form, selectedTachoType, usedTachoTypesForDriver]);

  if (!mappingType) return null;

  const effectiveTemperatureTypeOptions = temperatureTypeOptions.length
    ? temperatureTypeOptions?.map((single) => ({
        ...single,
        label: t(single.label),
        value: single.value,
      }))
    : temperatureTypeData?.map((single) => ({
        ...single,
        label: t(single.label),
        value: single.value,
      }));

  console.log({
    effectiveTemperatureTypeOptions,
    temperatureTypeOptionsLength: temperatureTypeOptions.length,
    temperatureTypeOptions,
    temperatureTypeData,
  });

  const gridClassName =
    variant === "drawer" || variant === "add-step-one" ? "contents" : "grid grid-cols-2 gap-4";

  const selectField = (
    name: string,
    label: string,
    options: MappingSelectOption[],
    requiredMessage?: string,
    placeholder?: string
  ) => (
    <Form.Item
      name={name}
      label={label}
      rules={requiredMessage ? [{ required: true, message: requiredMessage }] : undefined}
    >
      <CustomSelect
        options={selectOptions(options)}
        showSearch
        optionFilterProp="label"
        placeholder={placeholder ?? t("select_option")}
        className="common-select"
      />
    </Form.Item>
  );

  const inputField = (
    name: string,
    label: string,
    requiredMessage?: string,
    placeholder?: string
  ) => (
    <Form.Item
      name={name}
      label={label}
      rules={requiredMessage ? [{ required: true, message: requiredMessage }] : undefined}
    >
      <CustomInput className={textInputClassName} placeholder={placeholder} />
    </Form.Item>
  );

  const numberField = (
    name: string,
    label: string,
    requiredMessage?: string,
    initialValue?: number,
    addonAfter?: string
  ) => (
    <Form.Item
      name={name}
      label={label}
      initialValue={initialValue}
      rules={requiredMessage ? [{ required: true, message: requiredMessage }] : undefined}
    >
      <InputNumber className={numberInputClassName} addonAfter={addonAfter} />
    </Form.Item>
  );

  const medianWidthSlider = () => (
    <Form.Item name="medianWidth" label={t("median_width")} initialValue={3}>
      <Slider marks={medianWidthMarks} min={0} max={21} step={null} />
    </Form.Item>
  );

  switch (mappingType) {
    case "gsm_signal_strength":
      return selectField(
        "field_Type",
        `${t("type")}:`,
        signalStrengthFieldTypeOption,
        t("type_is_required")
      );

    case "power_source_voltage":
      return selectField(
        "field_Source",
        `${t("source")}:`,
        fieldSourceOptions,
        t("source_is_required")
      );

    case "external_power_events":
      return selectField(
        "field_Save_only_when_changes",
        `${t("save_only_when_changes")}:`,
        externalPowerEventsOptions
      );

    case "can_data":
      return (
        <div className={gridClassName}>
          {selectField(
            "canDataFieldType",
            `${t("type")}:`,
            fieldTypeOptions,
            t("type_is_required")
          )}
          {selectField("canDataConvert", `${t("convert_j1939")}:`, convertOptions)}
        </div>
      );

    case "relay_status":
      return (
        <div className={gridClassName}>
          {selectField(
            "relayId",
            t("id"),
            withDisabledOptions(fieldRelayIdOptions, relayUsedFieldMap),
            t("relay_ID_is_required")
          )}
          {inputField("relayLabel", t("relay_label"), t("relay_Label_is_required"))}
          {inputField("valueOnLabel", t("on_label"), undefined, "Deactive")}
          {inputField("valueOffLabel", t("off_label"), undefined, "Active")}
          {selectField(
            "type",
            `${t("type")}:`,
            relayOptions.length ? relayOptions : [],
            t("type_is_required")
          )}
          <Form.Item
            name="sendCommandWhileDriving"
            valuePropName="checked"
            label={t("allow_command_while_driving")}
            className="mapping-checkbox-form"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item name="disabledNotificationAlert" label={t("disable_notification_alert")}>
            <Radio.Group
              options={[
                { value: true, label: t("yes") },
                { value: false, label: t("no") },
              ]}
            />
          </Form.Item>
        </div>
      );

    case "pto":
      return (
        <div className={gridClassName}>
          {inputField("label", `${t("label")}:`, t("label_is_required"))}
          {inputField("valueOn", `${t("value_on")}:`, undefined, "ON")}
          {inputField("valueOff", `${t("value_off")}:`, undefined, "OFF")}
        </div>
      );

    case "save_value_for_readables_value":
      return inputField("readableValueName", `${t("name")}:`);

    case "switch_on_off_state":
      return (
        <div className={gridClassName}>
          {inputField("label", `${t("label")}:`)}
          {inputField("valueOnLabel", `${t("value_on")}:`, undefined, "ON")}
          {inputField("switchStateValueOff", `${t("value_off")}:`, undefined, "OFF")}
          {selectField(
            "switchStateUniqueId",
            `${t("unique_id")}:`,
            withDisabledOptions(switchStateUniqueIdOption, switchUsedFieldMap),
            t("unique_ID_is_required")
          )}
          {selectField(
            "switchStateFieldType",
            `${t("type")}:`,
            switchOptions,
            t("type_is_required")
          )}
          <Form.Item
            name="linkedToFuel"
            valuePropName="checked"
            label={t("linked_to_fuel")}
            className="mapping-checkbox-form"
          >
            <Checkbox />
          </Form.Item>
          {linkedToFuel && (
            <Form.Item
              name="fuel_consumed"
              label={`${t("fuel_consumed")}:`}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.reject(new Error(t("fuel_consumed_is_required")));
                    }

                    if (Number.isNaN(Number(value))) {
                      return Promise.reject(new Error(t("fuel_consumed_must_be_number")));
                    }

                    if (Number(value) <= 0) {
                      return Promise.reject(
                        new Error(t("fuel_consumed_must_be_greater_than_zero"))
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber className={numberInputClassName} addonAfter="L/h" />
            </Form.Item>
          )}
        </div>
      );

    case "temperature":
      return (
        <div className={gridClassName}>
          {inputField("label", `${t("label")}:`)}
          {selectField(
            "field_tank_ID",
            `${t("unique_id")}:`,
            withDisabledOptions(fuelTankIdFieldOptions, temperatureUniqueIdUsedFieldMap),
            t("unique_ID_is_required")
          )}
          <Form.Item
            name="unit"
            label={`${t("unit")}:`}
            initialValue="c"
            rules={[{ required: true, message: t("unit_is_required") }]}
          >
            <CustomSelect
              options={temperatureUnitData}
              showSearch
              optionFilterProp="label"
              className="common-select"
            />
          </Form.Item>
          {selectField(
            "type",
            `${t("type")}:`,
            effectiveTemperatureTypeOptions,
            t("type_is_required")
          )}{" "}
        </div>
      );

    case "weight_on_axis_single_value":
      return (
        <div className={gridClassName}>
          {inputField(
            "axisSingleValue",
            `${t("axle_starts_with_1")}:`,
            t("axle__starts_with_is_required")
          )}
          {inputField("wheelSingleValue", `${t("wheel_starts_with_0")}:`)}
        </div>
      );

    case "fuel_level_sensor_data":
      return (
        <div className={gridClassName}>
          {selectField(
            "fuelTankIdField",
            `${t("tank_id")}:`,
            withDisabledOptions(fuelTankIdFieldOptions, fuelTankUsedFieldMap),
            t("tank_ID_is_required")
          )}
          {inputField("label", `${t("label")}:`, t("label_is_required"))}
          {medianWidthSlider()}
          {numberField("maxIncSpeed", t("max_inc_speed_l_min"), undefined, 120)}
          {numberField("maxDecSpeed", t("max_dec_speed_l_min"), undefined, 60)}
          <Form.Item
            name="enableRefueling"
            valuePropName="checked"
            label={t("enable_refueling")}
            className="mapping-checkbox-form"
          >
            <Checkbox />
          </Form.Item>
        </div>
      );

    case "fuel_level_percentage_can":
    case "fuel_level_liter_can":
      return (
        <div className={gridClassName}>
          {inputField("label", `${t("label")}:`, undefined, "CAN")}
          {numberField(
            "tankCapacity",
            `${t("tank_capacity_l")}:`,
            t("tank_capacity__L_is_required"),
            120
          )}
          {medianWidthSlider()}
          {numberField("maxIncSpeed", t("max_inc_speed_l_min"), undefined, 120)}
          {numberField("maxDecSpeed", t("max_dec_speed_l_min"), undefined, 60)}
          <Form.Item
            name="enableRefueling"
            valuePropName="checked"
            label={t("enable_refueling")}
            className="mapping-checkbox-form"
          >
            <Checkbox />
          </Form.Item>
        </div>
      );

    case "fuel_catalyst_level_percentage_form":
      return null;

    case "tacho_working_state":
      return selectField(
        "working_state_driver",
        `${t("driver")}:`,
        driverData,
        t("driver_is_required")
      );

    case "tacho_related_state":
      return selectField(
        "related_state_driver",
        `${t("driver")}:`,
        driverData,
        t("driver_is_required")
      );

    case "tacho_driver_name":
      return selectField("name_driver", `${t("driver")}:`, driverData, t("driver_is_required"));

    case "tacho_driver_surname":
      return selectField("surname_driver", `${t("driver")}:`, driverData, t("surname_is_required"));

    case "tacho_card_id":
      return (
        <div className={gridClassName}>
          {selectField("driver_id_driver", `${t("driver")}:`, driverData, t("driver_is_required"))}
          {selectField(
            "driver_id_format",
            `${t("type")}:`,
            withDisabledOptions(driverIdFormat, usedTachoTypesForDriver),
            t("type_is_required")
          )}
        </div>
      );

    case "tacho_card_presence":
      return selectField(
        "card_presence_driver",
        `${t("driver")}:`,
        driverData,
        t("driver_is_required")
      );

    default:
      return null;
  }
};
